import React, { useState, useEffect} from 'react';
import {
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Platform
} from 'react-native';

import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { useAlertModal } from '../../../common/hooks';
import NumericInput from '../../../common/components/formcomponents/NumericInput';
import { addTransactionModaltyles } from './styles/AddTransactionModalStyle';
import { RadioButton } from 'react-native-paper';
import { refundStripe } from '../../../api/Stripe';
import { msgStr } from '../../../common/constants/Message';

interface RefundStripeModalProps {
  isModalVisible: boolean;
  refundDetails: any;
  closeModal: () => void;
  onRefunded?: () => void;
}

const RefundStripeModal = ({
  isModalVisible,
  refundDetails,
  closeModal,
  onRefunded,
}: RefundStripeModalProps) => {

  const { showAlert } = useAlertModal();
  const [isLoading, setIsLoading] = useState(false);
  const [option, setOption] = useState<number>(1);
  const [amount, setAmount] = useState<number>(0);

  const fmt = (val: number) => (val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const paymentAmount = parseFloat(refundDetails.amount || 0);
  const alreadyRefunded = parseFloat(refundDetails.old_refunded || 0);
  const serviceFee = parseFloat(refundDetails.service_fee || 0);
  const bodhiProtect = parseFloat(refundDetails.bodhi_protect_price || 0);
  // Service Fee and Bodhi Protect are normally non-refundable.
  const totalFees = serviceFee + bodhiProtect;

  // What this charge still has available to refund (amount minus prior refunds).
  const thisChargeRemaining = Math.max(0, parseFloat((paymentAmount - alreadyRefunded).toFixed(2)));

  // Fees are tracked PER TRANSACTION on the payment row, so this transaction's
  // non-refundable fees are simply its own captured Service Fee + Bodhi Protect.
  const thisTransactionFees = Math.min(totalFees, thisChargeRemaining);

  // Fees are NEVER refundable. The refundable amount is whatever is left on this
  // charge after protecting its own fees. Both 'All' and 'Partial' respect this.
  const refundableExclFees = Math.max(0, parseFloat((thisChargeRemaining - thisTransactionFees).toFixed(2)));
  const maxRefundable = refundableExclFees;

  // Only this transaction's fees remain — nothing is refundable.
  const onlyFeesRemain = thisChargeRemaining > 0 && refundableExclFees <= 0;

  // A suggested amount to return (e.g. a reschedule/downsize credit). When present,
  // open in Partial mode prefilled to that amount (capped by what's refundable).
  const prefillAmount = parseFloat(refundDetails.prefill_amount || 0);

  // 'All' defaults to the fee-excluded refundable amount (the recommended refund).
  useEffect(()=>{
    if (option === 1) setAmount(refundableExclFees);
  }, [isModalVisible, option, refundableExclFees])

  // On open, if a prefill (credit to return) was passed, switch to Partial and
  // preset the amount to the credit, capped by the fee-protected refundable.
  useEffect(() => {
    if (!isModalVisible) return;
    if (prefillAmount > 0) {
      setOption(2);
      setAmount(Math.min(prefillAmount, maxRefundable));
    } else {
      setOption(1);
    }
  }, [isModalVisible])

  const refund = async () =>{
    const refundAmount = Math.min(parseFloat(amount as any) || 0, maxRefundable);
    if (refundAmount <= 0) {
      showAlert('error', 'Nothing is refundable on this payment.');
      return;
    }
    const payload = {
      ...refundDetails,
      // Always send an explicit amount so we refund exactly what's shown
      // ('All' = fee-excluded amount, 'Partial' = up to the full payment).
      option: 2,
      manual_amount: refundAmount,
    }
    refundStripe(payload, (jsonRes, status)=>{
      switch (status) {
        case 200:
          showAlert('success', 'Refunded successfully');
          if (onRefunded) onRefunded();
          closeModal();
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          closeModal();
          break;
      }
    });
  }

  const closeModalhandler = () => {
    closeModal();
  };

  return isModalVisible ? (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer contentStyle={{ width: 520, maxWidth: '92%', padding: 20 }}>
        <ModalHeader
          label={'Refund Stripe'}
          closeModal={() => {
            closeModalhandler();
          }}
        />
        <ModalBody style={{ zIndex: 10 }}>
          {/* Summary: payment, already refunded, fees, and the refundable amount. */}
          <View style={{ backgroundColor: '#f7f7f7', borderRadius: 10, paddingHorizontal: 18, paddingVertical: 16, marginTop: 10, marginBottom: 14 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 }}>
              <Text style={{ color: '#333', fontSize: 16 }}>{'Payment'}</Text>
              <Text style={{ color: '#333', fontSize: 16 }}>{fmt(paymentAmount)}</Text>
            </View>
            {alreadyRefunded > 0 && (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 }}>
                <Text style={{ color: '#6c757d', fontSize: 16 }}>{'Already refunded'}</Text>
                <Text style={{ color: '#6c757d', fontSize: 16 }}>{'-' + fmt(alreadyRefunded)}</Text>
              </View>
            )}
            {serviceFee > 0 && (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 }}>
                <Text style={{ color: '#DC3545', fontSize: 16 }}>{'Service Fee (non-refundable)'}</Text>
                <Text style={{ color: '#DC3545', fontSize: 16 }}>{'-' + fmt(serviceFee)}</Text>
              </View>
            )}
            {bodhiProtect > 0 && (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 }}>
                <Text style={{ color: '#DC3545', fontSize: 16 }}>{'Bodhi Protect (non-refundable)'}</Text>
                <Text style={{ color: '#DC3545', fontSize: 16 }}>{'-' + fmt(bodhiProtect)}</Text>
              </View>
            )}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, borderTopWidth: 1, borderTopColor: '#ddd', marginTop: 8, paddingTop: 12 }}>
              <Text style={{ color: '#28A745', fontWeight: '700', fontSize: 19 }}>{'Refundable'}</Text>
              <Text style={{ color: '#28A745', fontWeight: '700', fontSize: 22 }}>{fmt(refundableExclFees)}</Text>
            </View>
          </View>
          {thisTransactionFees > 0 && maxRefundable > 0 && (
            <Text style={{ fontSize: 13, color: '#999', marginBottom: 14 }}>
              {`"Refundable" excludes this payment's ${fmt(thisTransactionFees)} in non-refundable fees (Service Fee + Bodhi Protect). These fees can never be refunded.`}
            </Text>
          )}
          {onlyFeesRemain && (
            <Text style={{ fontSize: 13, color: '#DC3545', marginBottom: 14, fontWeight: '600' }}>
              {`Only non-refundable fees remain. No refund is possible.`}
            </Text>
          )}

          {!onlyFeesRemain && (
            <View style={{flexDirection:'row', justifyContent:'flex-start', marginTop:6, marginBottom:16}}>
              <View style={{flexDirection:'row', alignItems:'center', justifyContent:'center'}}>
                <RadioButton
                  value={option.toString()}
                  status={option == 1? 'checked': 'unchecked'}
                  onPress={()=>{setOption(1); setAmount(refundableExclFees);}}
                  color="#0099ff"
                />
                <Text style={{ fontSize: 16, marginRight: 36 }}>{"All (excl. fees)"}</Text>
              </View>
              <View style={{flexDirection:'row', alignItems:'center', justifyContent:'center'}}>
                <RadioButton
                  value={option.toString()}
                  status={option == 2? 'checked': 'unchecked'}
                  onPress={()=>setOption(2)}
                  color="#0099ff"
                />
                <Text style={{ fontSize: 16 }}>{"Partial"}</Text>
              </View>
            </View>
          )}
          {!onlyFeesRemain && (
            <>
              <TouchableOpacity 
                onPress={() => {
                  if (option === 2) {
                    setAmount(maxRefundable);
                  }
                }}
                disabled={option === 1}
              >
                <Text style={[styles.label, { fontSize: 16 }]}>
                  {option === 1
                    ? `Amount (${fmt(refundableExclFees)} \u2014 fees excluded)`
                    : `Amount (max ${fmt(maxRefundable)}) — tap to fill`}
                </Text>
              </TouchableOpacity>
              <NumericInput
                editable={option === 1? false: true}
                validMinNumber={0}
                validMaxNumber={maxRefundable}
                style={[styles.input, {textAlign:'left', fontSize: 18, paddingVertical: 12}, option === 1 && {color:'#ccc', borderColor:'#ccc'}]}
                placeholder="Amount"
                value={amount?amount:0}
                onChangeText={(val)=>{
                  // NumericInput already blocks out-of-range values from the display
                  // via validMaxNumber; guard the stored value too so it never exceeds.
                  const num = parseFloat(val as any);
                  if (!isNaN(num) && num > maxRefundable) {
                    return;
                  }
                  setAmount(val);
                }}
                placeholderTextColor="#ccc"
              />
            </>
          )}
          {!onlyFeesRemain && (
            <Text style={{ fontSize: 13, color: '#999', marginTop: 10 }}>
              {`Partial lets you refund a smaller custom amount, up to ${fmt(maxRefundable)}. Fees are always protected.`}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <View style={{ flexDirection: 'row', width:'100%', alignItems:'center', justifyContent:'flex-end' }}>
            <View style={{ flexDirection: 'row', alignItems:'center',}}>
              <TouchableOpacity onPress={closeModalhandler}>
                <Text style={[styles.addButton]}>{'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                disabled={(Math.min(parseFloat(amount as any) || 0, maxRefundable)) <= 0}
                onPress={()=>{refund()}}
              >
                <Text style={[styles.addButton, ((Math.min(parseFloat(amount as any) || 0, maxRefundable)) <= 0) && { backgroundColor: '#ccc', color: '#fff' }]}>{"Refund"}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ModalFooter>
      </BasicModalContainer>

      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </View>
  ) : null;
};

const styles = addTransactionModaltyles;

export default RefundStripeModal;
